import type {StorageRoot} from './types'

/** A folder listing in the user's own space, keyed by the prefix it lists. */
export type OwnFilesListQueryKey = ['files', {filePath: string}]
/** A folder listing in a shared folder. */
export type ShareFilesListQueryKey = [
  'files',
  'share',
  string,
  {identityId: string; folder: string}
]
export type FilesListQueryKey = OwnFilesListQueryKey | ShareFilesListQueryKey

/**
 * The own-space listing key, from the `${identityId}/${folder}` prefix the listing request sends.
 * Kept for the live-refresh code, which builds those prefixes (see `buildFolderLineageKeys`).
 */
export const filesListQueryKey = (filePath: string): OwnFilesListQueryKey => ['files', {filePath}]

/**
 * Every React Query key for file listings. Build them here, never by hand: an invalidation only
 * reaches a listing whose key it matches, so two spellings of one key are a stale-folder bug.
 *
 * React Query matches keys by prefix, and an object element by its own fields. So:
 * - {@link all} reaches every listing, of every root.
 * - An own-space key never reaches a shared folder's listing: their second elements differ in type.
 * - {@link share} reaches the listings of that one shared folder, and nothing else.
 */
export const fileQueryKeys = {
  all: ['files'] as const,
  /**
   * One folder's listing. `identityId` is the signed-in user's. `folder` is relative to the root,
   * without slashes at either end: `''` for the root, `a/b` below it.
   *
   * For the own root this is `['files', {filePath: '<identityId>/<folder>'}]`, the key the Files
   * browser has always used, so cached listings and live refreshes carry over unchanged.
   */
  list: (root: StorageRoot, identityId: string, folder: string): FilesListQueryKey => {
    switch (root.kind) {
      case 'own':
        return filesListQueryKey(`${identityId}/${folder}`)
      case 'share':
        return ['files', 'share', root.id, {identityId, folder}]
    }
  },
  /** Every listing of one shared folder, to drop them together (for instance on a 403). */
  share: (shareId: string) => ['files', 'share', shareId] as const,
  /**
   * One folder of a shared folder, whoever is signed in: matches the {@link list} key of that
   * folder by prefix. `folder` is in {@link list}'s form (`''`, `a/b`).
   */
  shareFolder: (shareId: string, folder: string) => ['files', 'share', shareId, {folder}] as const,
}
